import { SignalFilled } from '@compass/Icon'
import { QueryFilterDefinition, QueryFilterMenuSection, QueryFilterOption } from 'components/QueryFilters'
import { useWorkspaceEnvironments } from 'hooks/api/environment'
import { Bot, Box, Fingerprint, GlobeLockIcon, Grid2X2, IdCardLanyardIcon, ServerIcon } from 'lucide-react'
import { useMemo, useRef } from 'react'

import {
  BOT_CATEGORY_ICONS,
  BOT_CATEGORY_LABELS,
  BOT_CONFIDENCE_LABELS,
  BOT_CONFIDENCE_SEVERITY,
  BOT_IDENTITY_COLORS,
  BOT_IDENTITY_ICONS,
  BOT_IDENTITY_LABELS,
  BOT_PROVIDER_DEFINITIONS,
  getAIBotLogo,
  getBotProviderLabel,
  getBotProviderLogo,
} from '../consts'
import { BotCategory, BotConfidence, BotDetectionEventsFilter, BotIdentity } from '../models'

export const BOT_DETECTION_OVERVIEW_FILTER_KEYS = [
  'botName',
  'category',
  'provider',
  'identity',
  'confidence',
  'proxy',
  'vpn',
  'environmentIds',
] as const satisfies readonly (keyof BotDetectionEventsFilter)[]

export type BotDetectionOverviewFilterKey = (typeof BOT_DETECTION_OVERVIEW_FILTER_KEYS)[number]
export type BotDetectionOverviewFilterValues = Pick<BotDetectionEventsFilter, BotDetectionOverviewFilterKey>

const BOT_CATEGORY_VALUES = Object.keys(BOT_CATEGORY_LABELS) as BotCategory[]
const BOT_CONFIDENCE_VALUES = Object.keys(BOT_CONFIDENCE_LABELS) as BotConfidence[]

const categoryOptions = BOT_CATEGORY_VALUES.map((value) => {
  const Icon = BOT_CATEGORY_ICONS[value]

  return {
    id: value,
    name: BOT_CATEGORY_LABELS[value],
    icon: <Icon size={16} />,
  }
})
const identityOptions = (Object.keys(BOT_IDENTITY_LABELS) as BotIdentity[]).map((value) => {
  const Icon = BOT_IDENTITY_ICONS[value]

  return {
    id: value,
    name: BOT_IDENTITY_LABELS[value],
    icon: <Icon size={16} stroke={BOT_IDENTITY_COLORS[value].icon} />,
  }
})
const confidenceOptions = BOT_CONFIDENCE_VALUES.map((value) => {
  const { bars, color } = BOT_CONFIDENCE_SEVERITY[value]

  return {
    id: value,
    name: BOT_CONFIDENCE_LABELS[value],
    icon: <SignalFilled filledBars={bars} size={16} fill={color} />,
  }
})

function withProviderLabelsAndIcons(options: QueryFilterOption[]): QueryFilterOption[] {
  return options.map((option) => {
    const Logo = getBotProviderLogo(option.id)

    return {
      ...option,
      name: getBotProviderLabel(option.id),
      icon: <Logo size={16} />,
    }
  })
}

function mergeSelectedOptions(options: QueryFilterOption[], selectedValues?: string[]) {
  const optionIds = new Set(options.map((option) => option.id))
  const selectedOptions = (selectedValues ?? [])
    .filter((value) => !optionIds.has(value))
    .map((value) => ({ id: value, name: value }))

  return [...options, ...selectedOptions].sort((left, right) => left.name.localeCompare(right.name))
}

function mergeRememberedOptions(previousOptions: QueryFilterOption[], nextOptions: QueryFilterOption[]) {
  const optionsById = new Map(previousOptions.map((option) => [option.id, option]))

  nextOptions.forEach((option) => {
    optionsById.set(option.id, option)
  })

  return [...optionsById.values()].sort((left, right) => left.name.localeCompare(right.name))
}

const hasFilterValue = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.length > 0
  }
  return value !== undefined && value !== ''
}

export function getActiveBotDetectionOverviewFilters(
  filterValues: Partial<BotDetectionOverviewFilterValues>
): Partial<BotDetectionOverviewFilterValues> {
  return Object.fromEntries(
    BOT_DETECTION_OVERVIEW_FILTER_KEYS.flatMap((filterKey) => {
      const value = filterValues[filterKey]

      return hasFilterValue(value) ? [[filterKey, value]] : []
    })
  ) as Partial<BotDetectionOverviewFilterValues>
}

export function getClearedBotDetectionOverviewFilters(
  filterDefinitions: Record<BotDetectionOverviewFilterKey, QueryFilterDefinition<BotDetectionOverviewFilterValues>>
): Partial<Record<BotDetectionOverviewFilterKey, undefined>> {
  return Object.fromEntries(
    Object.values(filterDefinitions).map((filterDefinition) => [filterDefinition.id, undefined])
  )
}

export function getAppliedBotDetectionOverviewFilterCount(filterValues: Partial<BotDetectionOverviewFilterValues>) {
  return Object.values(filterValues).filter(hasFilterValue).length
}

export function useBotDetectionOverviewFilter({
  subscriptionId,
  filterValues,
  botNames = [],
}: {
  subscriptionId: string
  filterValues: Partial<BotDetectionOverviewFilterValues>
  botNames?: string[]
}) {
  const { data: environments } = useWorkspaceEnvironments(subscriptionId)
  const environmentOptions = useMemo(
    () => (environments ?? []).map((environment) => ({ id: environment.id, name: environment.name })),
    [environments]
  )
  const botNameOptionsRef = useRef<QueryFilterOption[]>([])

  const providerOptions = useMemo(() => {
    const baseOptions = BOT_PROVIDER_DEFINITIONS.map(({ slug, label }) => ({
      id: slug,
      name: label,
    }))

    return withProviderLabelsAndIcons(mergeSelectedOptions(baseOptions, filterValues.provider))
  }, [filterValues.provider])
  const botNameOptions = useMemo(() => {
    const currentBotNameSet = new Set(botNames)
    const nextOptions = mergeSelectedOptions(
      botNames.map((name) => ({ id: name, name })),
      filterValues.botName
    ).map((option) => {
      const Logo = getAIBotLogo({ name: option.id })
      return { ...option, icon: <Logo size={16} /> }
    })

    const filteredPrevious = botNameOptionsRef.current.filter(
      (option) => currentBotNameSet.has(option.id) || filterValues.botName?.includes(option.id)
    )
    botNameOptionsRef.current = mergeRememberedOptions(filteredPrevious, nextOptions)
    return botNameOptionsRef.current
  }, [botNames, filterValues.botName])

  const filterDefinitions = useMemo<
    Record<BotDetectionOverviewFilterKey, QueryFilterDefinition<BotDetectionOverviewFilterValues>>
  >(() => {
    return {
      botName: {
        id: 'botName',
        label: 'Bot name',
        inputType: 'checklist',
        value: filterValues.botName ?? [],
        options: botNameOptions,
        searchableOptions: true,
        allowCustomValue: true,
        placeholder: 'Filter or enter bot name',
        tooltip: 'Filter overview metrics by bot name.',
        icon: <Bot size={16} />,
      },
      category: {
        id: 'category',
        label: 'Category',
        inputType: 'checklist',
        value: filterValues.category ?? [],
        options: categoryOptions,
        searchableOptions: true,
        placeholder: 'Filter category',
        tooltip: 'Filter overview metrics by bot category.',
        icon: <Grid2X2 size={16} />,
      },
      provider: {
        id: 'provider',
        label: 'Provider',
        inputType: 'checklist',
        value: filterValues.provider ?? [],
        options: providerOptions,
        searchableOptions: true,
        allowCustomValue: true,
        placeholder: 'Filter or enter provider',
        tooltip: 'Filter overview metrics by bot provider.',
        icon: <IdCardLanyardIcon size={16} />,
      },
      identity: {
        id: 'identity',
        label: 'Identity',
        inputType: 'checklist',
        value: filterValues.identity ?? [],
        options: identityOptions,
        searchableOptions: true,
        placeholder: 'Filter identity',
        tooltip: 'Filter overview metrics by bot identity.',
        icon: <Fingerprint size={16} />,
      },
      confidence: {
        id: 'confidence',
        label: 'Confidence',
        inputType: 'checklist',
        value: filterValues.confidence ?? [],
        options: confidenceOptions,
        searchableOptions: true,
        placeholder: 'Filter confidence',
        tooltip: 'Filter overview metrics by bot confidence level.',
        icon: <SignalFilled size={16} filledBars={3} />,
      },
      proxy: {
        id: 'proxy',
        label: 'Proxy',
        inputType: 'boolean',
        value: filterValues.proxy,
        tooltip: 'Filter overview metrics by proxy signal status.',
        icon: <ServerIcon size={16} />,
      },
      vpn: {
        id: 'vpn',
        label: 'VPN',
        inputType: 'boolean',
        value: filterValues.vpn,
        tooltip: 'Filter overview metrics by VPN signal status.',
        icon: <GlobeLockIcon size={16} />,
      },
      environmentIds: {
        id: 'environmentIds',
        label: 'Environment',
        inputType: 'checklist',
        value: filterValues.environmentIds ?? [],
        options: environmentOptions,
        searchableOptions: true,
        placeholder: 'Filter environment',
        tooltip: 'Filter overview metrics by environment.',
        icon: <Box size={16} />,
      },
    }
  }, [botNameOptions, filterValues, providerOptions, environmentOptions])

  const filterMenuLayout = useMemo<QueryFilterMenuSection<BotDetectionOverviewFilterValues>[]>(
    () => [
      {
        id: 'bot',
        items: [
          filterDefinitions.botName,
          filterDefinitions.category,
          filterDefinitions.provider,
          filterDefinitions.identity,
          filterDefinitions.confidence,
        ],
      },
      {
        id: 'network',
        items: [filterDefinitions.proxy, filterDefinitions.vpn],
      },
      {
        id: 'environment',
        items: [filterDefinitions.environmentIds],
      },
    ],
    [filterDefinitions]
  )

  return { filterDefinitions, filterMenuLayout }
}
